/**
 * Synchro temps réel via le relay WebSocket NATS de l'api-central (`GET /ws`).
 *
 * Sur chaque CloudEvent tasks reçu, on invalide le cache TanStack Query :
 * les écrans re-fetchent et reflètent les mutations faites depuis n'importe
 * quel autre client (Workforce, autre onglet, autre utilisateur) sans reload.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { User } from "oidc-client-ts";
import { env } from "./env";
import { getAccessToken, userManager } from "./auth/oidc";

/** subject NATS souscrit → préfixes de queryKey à invalider */
const SUBJECT_MAP: ReadonlyArray<{ subject: string; keys: string[][] }> = [
  {
    subject: `nesit.${env.VITE_TENANT_ID}.tasks.>`,
    keys: [["tm"]],
  },
];

function invalidateForSubject(qc: QueryClient, subject: string): void {
  for (const entry of SUBJECT_MAP) {
    // le préfixe hors wildcard suffit à router (`nesit.<tenant>.tasks.`)
    if (subject.startsWith(entry.subject.slice(0, -1))) {
      for (const key of entry.keys) {
        void qc.invalidateQueries({ queryKey: key });
      }
    }
  }
}

function invalidateAll(qc: QueryClient): void {
  for (const entry of SUBJECT_MAP) {
    for (const key of entry.keys) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }
}

function buildWsUrl(token: string): string {
  const url = new URL("/ws", env.VITE_API_CENTRAL_BASE_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // les navigateurs n'autorisent pas de header custom au handshake WS
  url.searchParams.set("token", token);
  return url.toString();
}

function startRealtime(qc: QueryClient): () => void {
  let ws: WebSocket | null = null;
  let stopped = false;
  let attempt = 0;
  let everConnected = false;
  let retryTimer: number | undefined;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = Math.min(30_000, 1000 * 2 ** attempt++);
    retryTimer = window.setTimeout(() => void connect(), delay);
  };

  const connect = async (): Promise<void> => {
    let token: string | null;
    try {
      token = await getAccessToken();
    } catch {
      // un throw (sessionStorage indispo, renew échoué) ne doit pas tuer la
      // boucle de retry : sans ce catch, `void connect()` avale le rejet.
      scheduleRetry();
      return;
    }
    if (stopped) return;
    if (!token) {
      scheduleRetry();
      return;
    }
    ws = new WebSocket(buildWsUrl(token));
    ws.onopen = () => {
      attempt = 0;
      ws?.send(
        JSON.stringify({
          type: "subscribe",
          subjects: SUBJECT_MAP.map((e) => e.subject),
        }),
      );
      // rattrape les events manqués pendant une déconnexion
      if (everConnected) invalidateAll(qc);
      everConnected = true;
    };
    ws.onmessage = (e: MessageEvent) => {
      let msg: { type?: unknown; subject?: unknown };
      try {
        msg = JSON.parse(String(e.data)) as typeof msg;
      } catch {
        return;
      }
      if (msg.type === "event" && typeof msg.subject === "string") {
        invalidateForSubject(qc, msg.subject);
      }
    };
    ws.onclose = () => {
      ws = null;
      scheduleRetry();
    };
  };

  // pousse le JWT renouvelé au relay avant expiry (sinon close 4401)
  const onUserLoaded = (user: User): void => {
    if (ws?.readyState === WebSocket.OPEN && user.access_token) {
      ws.send(JSON.stringify({ type: "refresh", token: user.access_token }));
    }
  };
  userManager.events.addUserLoaded(onUserLoaded);

  void connect();

  return () => {
    stopped = true;
    window.clearTimeout(retryTimer);
    userManager.events.removeUserLoaded(onUserLoaded);
    ws?.close(1000);
    ws = null;
  };
}

/** À monter une fois, sous QueryClientProvider (cf. App.tsx). */
export function useRealtimeSync(): void {
  const qc = useQueryClient();
  useEffect(() => startRealtime(qc), [qc]);
}
